// Story 11.2 — Helpers para extraer un flow desde el contenido del editor.
// `parseFlowFromObject` es puro (testeado sin deps). `parseFlowYaml` agrega
// la deserialización YAML en runtime via dynamic import — los tests cubren
// la pura.

import type { FlowShape, StepShape } from '@/components/editor/diagram/flow-to-graph';

// P-369 (review 11.2): permitir flows nested (`flows/onboarding/welcome.yaml`).
// Antes el regex `^flows\/([\w-]+)\.yaml$` rechazaba subdirectorios.
const FLOW_PATH_REGEX = /^flows\/(.+)\.yaml$/;

/** True si el path es un archivo de flow editable. */
export function isFlowPath(path: string | null | undefined): boolean {
  if (!path) return false;
  return FLOW_PATH_REGEX.test(path);
}

/**
 * Extrae el flow name desde un path. Para paths nested (`flows/a/b.yaml`)
 * retorna el último segmento (`b`) — el flow name canónico es el del archivo,
 * no la jerarquía de carpetas.
 */
export function flowNameFromPath(path: string | null | undefined): string | null {
  if (!path) return null;
  const m = FLOW_PATH_REGEX.exec(path);
  if (!m) return null;
  const rel = m[1] ?? '';
  const lastSlash = rel.lastIndexOf('/');
  return lastSlash >= 0 ? rel.slice(lastSlash + 1) : rel;
}

export interface FlowParseResult {
  /** Flow listo para `flowToGraph`, o null si el contenido no era flow válido. */
  flow: FlowShape | null;
  error: string | null;
}

/**
 * Pure: dado un objeto ya parseado (típicamente desde YAML), busca el flow
 * en las shapes canónicas:
 *   - `{ <flowName>: { steps: [...] } }`
 *   - `{ steps: [...] }`
 *   - `{ <onlyKey>: { steps: [...] } }` (cuando flowName no se conoce)
 */
export function parseFlowFromObject(parsed: unknown, flowName: string | null): FlowParseResult {
  if (parsed == null) {
    return { flow: { steps: [] }, error: null };
  }
  if (typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { flow: null, error: 'YAML root debe ser un objeto' };
  }
  const obj = parsed as Record<string, unknown>;

  if (flowName && obj[flowName] && typeof obj[flowName] === 'object') {
    const inner = obj[flowName] as Record<string, unknown>;
    if (Array.isArray(inner.steps)) {
      return { flow: { steps: inner.steps as StepShape[] }, error: null };
    }
  }
  if (Array.isArray(obj.steps)) {
    return { flow: { steps: obj.steps as StepShape[] }, error: null };
  }
  const keys = Object.keys(obj);
  if (keys.length === 1 && keys[0]) {
    const inner = obj[keys[0]];
    if (inner && typeof inner === 'object' && Array.isArray((inner as { steps?: unknown }).steps)) {
      return {
        flow: { steps: (inner as { steps: StepShape[] }).steps },
        error: null,
      };
    }
  }
  return { flow: null, error: 'No se encontró `steps` en el YAML' };
}

export interface DerivedFlowResult {
  /** Flow listo para `flowToGraph`, o null si el YAML no es válido. */
  flow: FlowShape | null;
  /** True si la tab activa NO es un flow (UI muestra placeholder). */
  notAFlow: boolean;
  /** Error de parsing si lo hubo. */
  error: string | null;
}

/**
 * Pure: deriva el resultado del hook a partir del path activo y el objeto
 * pre-parseado. Útil para tests sin React.
 */
export function deriveFlowFromObject(
  activePath: string | null,
  parsed: unknown,
): DerivedFlowResult {
  if (!activePath || !isFlowPath(activePath)) {
    return { flow: null, notAFlow: true, error: null };
  }
  const flowName = flowNameFromPath(activePath);
  const res = parseFlowFromObject(parsed, flowName);
  return { flow: res.flow, notAFlow: false, error: res.error };
}

/**
 * Runtime: parsea YAML via `yaml@^2` y delega a `parseFlowFromObject`. Usa
 * dynamic import para que no se cargue en builds donde solo se usa la
 * versión pura.
 */
export async function parseFlowYaml(
  content: string,
  flowName: string | null,
): Promise<FlowParseResult> {
  if (!content || !content.trim()) {
    return { flow: { steps: [] }, error: null };
  }
  try {
    const mod = (await import('yaml')) as typeof import('yaml');
    const parsed = mod.parse(content) as unknown;
    return parseFlowFromObject(parsed, flowName);
  } catch (err) {
    return { flow: null, error: (err as Error).message ?? 'YAML parse error' };
  }
}
